/**
 * Shortcut catalog — the single source of truth for every keyboard shortcut's
 * display metadata (label, description, whether it can be rebound, its default
 * binding).
 *
 * Consumed by, and ONLY by, surfaces that show or bind a shortcut:
 *
 *   • `KeyboardShortcutsDialog` — renders every entry as a read-only reference.
 *   • `ShortcutsCard` (Settings → Personal → General) — renders the rebindable
 *     entries with Change… / Reset chrome and the fixed ones read-only.
 *   • `SidebarSearchTrigger` / `SidebarCollapseToggle` — print the live binding
 *     as a row hint.
 *   • `Sidebar` — actually listens for the sidebar-toggle binding.
 *
 * ## Why this file exists
 *
 * Ported from Vergence's `lib/controls/catalog.ts`, whose docblock records the
 * failure it was written to end: its shortcuts dialog and its settings page each
 * carried their own hardcoded labels, and they drifted — one called the same
 * action "Toggle composer dictation", the other "Toggle dictation" and filed it
 * under a category that did not exist anywhere else. Two lists of shortcut names
 * cannot be kept in agreement by discipline. There is one list here, and every
 * surface reads it.
 *
 * The same argument applies to the BINDING, not just the label: `SidebarSearchTrigger`
 * printed "⌘K" from its own constant while `Sidebar` matched ⌘B from another. A row
 * hint that is a separate literal from the handler it advertises is a lie waiting
 * to happen the first time either changes.
 *
 * ## No categories
 *
 * Vergence dropped them and the reason transfers: every shortcut here is global,
 * so any grouping would be invented rather than observed. If one genuinely earns a
 * category later, add a `category` field here and group in both consumers — same
 * pattern, still one list.
 */

/** Keys the user may rebind. Narrower than `ShortcutKey` on purpose: it is what
 *  indexes the stored bindings object, so a non-rebindable key cannot be written
 *  to storage by mistake. */
export type RebindableShortcutKey =
  | 'search'
  | 'sidebarToggle'
  | 'askToggle'
  | 'askNewChat'
  | 'askDictation'

/** Shortcuts whose binding is hardcoded in a handler rather than stored. */
import { IS_APPLE_PLATFORM } from './shortcuts'

export type FixedShortcutKey = 'openShortcutsDialog' | 'dismiss' | 'browserZoom'

/** Every catalogued shortcut, rebindable or not. */
export type ShortcutKey = RebindableShortcutKey | FixedShortcutKey

interface ShortcutCatalogEntryBase {
  /** Shown verbatim in the dialog, the settings card, and (for the sidebar rows)
   *  as the accessible name's shortcut. */
  label: string
  /** One line under the label. Complete sentence — the dialog wraps rather than
   *  truncates, because a description cut mid-word has no reference value. */
  description: string
  /**
   * Chord grammar for rebindable entries: `mod+k`, `mod+shift+b` — `mod` being
   * ⌘ on Apple and Ctrl elsewhere, so one stored string is correct on both.
   *
   * For fixed entries this is a literal display token instead (`?`, `Esc`),
   * because those keys are not chords and inventing a chord spelling for them
   * would only be decorative.
   */
  defaultBinding: string
}

/** The settings card gives these Change… / Reset, and their live value comes from
 *  stored bindings. */
export interface RebindableShortcutEntry extends ShortcutCatalogEntryBase {
  key: RebindableShortcutKey
  rebindable: true
}

/** Rendered read-only on both surfaces — the binding is in a handler, not storage. */
export interface FixedShortcutEntry extends ShortcutCatalogEntryBase {
  key: FixedShortcutKey
  rebindable: false
}

/**
 * A union rather than a `rebindable: boolean` field, so that `entry.rebindable`
 * NARROWS `entry.key` to the rebindable subset. That is what makes
 * `bindings[entry.key]` type-check without a cast at every consumer — the
 * compiler, not a comment, enforces that only rebindable keys index storage.
 */
export type ShortcutCatalogEntry = RebindableShortcutEntry | FixedShortcutEntry

export const SHORTCUT_CATALOG: readonly ShortcutCatalogEntry[] = [
  {
    key: 'search',
    label: 'Search',
    description: 'Opens the search overlay from anywhere in the platform.',
    rebindable: true,
    // ⌘K is the near-universal palette binding (Linear, GitHub, Slack, Notion).
    defaultBinding: 'mod+k',
  },
  {
    key: 'sidebarToggle',
    label: 'Collapse / expand sidebar',
    description: 'Pins the left sidebar open or collapsed, whichever it is not showing now.',
    rebindable: true,
    // ⌘B is the universal sidebar binding, and clear of ⌘K above.
    defaultBinding: 'mod+b',
  },
  {
    key: 'askToggle',
    label: 'Toggle Ask Vera',
    description: 'Opens or closes the Ask Vera panel from anywhere in the platform.',
    rebindable: true,
    // ⌘J (this app's obvious first pick, adjacent to ⌘K above) is Chrome/Edge's own
    // "Show downloads" on Windows and Linux — a real, page-visible browser binding,
    // not merely a menu item. ⌘I collides with nothing at the browser-chrome level on
    // either platform, and is clear of ⌘⇧I (DevTools' element inspector), which is a
    // different chord.
    defaultBinding: 'mod+i',
  },
  {
    key: 'askNewChat',
    label: 'New chat',
    description: 'Starts a fresh Ask Vera conversation, opening the panel first if it is closed.',
    rebindable: true,
    // ⌘⇧O — the natural pairing with the panel toggle above and Vergence's own
    // convention — is Chrome/Edge's Bookmark Manager on Windows and Linux. ⌘⇧Enter
    // has no browser- or editor-level meaning to collide with (Shift+Enter alone
    // means "newline" in the composer, a different, non-modded chord).
    defaultBinding: 'mod+shift+enter',
  },
  {
    key: 'askDictation',
    label: 'Start / stop dictation',
    description: 'Starts or stops voice dictation into the Ask Vera composer. Works even while the composer has focus.',
    rebindable: true,
    // Firefox binds ⌘⇧D to "Bookmark All Tabs", but the Web Speech API this feature
    // is built on is Chromium/WebKit-only — Firefox never shows the mic button in the
    // first place, so there is nothing here for that binding to compete with there.
    defaultBinding: 'mod+shift+d',
  },
  {
    key: 'openShortcutsDialog',
    label: 'Show keyboard shortcuts',
    description: 'Opens this list from anywhere, except while you are typing in a field.',
    rebindable: false,
    defaultBinding: '?',
  },
  {
    /**
     * The BROWSER's own zoom, listed here because users reach for it expecting Vera to
     * own it, and then cannot find it in this list.
     *
     * Vera does not bind these and must not: they are the browser's, they work on every
     * site, and rebinding them would break a control people already rely on. Listing them
     * as fixed entries is the honest treatment — it answers "how do I make this bigger"
     * at the moment the question is asked, and points at Settings -> Appearance -> Text
     * size for the in-app control, which differs in one way worth knowing: browser zoom
     * scales the viewport, while Text size scales the interface and persists per browser.
     */
    key: 'browserZoom',
    label: 'Zoom in or out',
    description:
      "Your browser's own zoom, not Vera's. It works everywhere. For an in-app setting that persists, use Settings, Appearance, Text size.",
    rebindable: false,
    /**
     * A DISPLAY literal, not a chord — deliberately.
     *
     * This row describes TWO keys, and `defaultBinding` holds one. `formatBinding`
     * returns anything that is not a valid chord unchanged, so a compound string like
     * `'mod+plus / mod+minus'` renders raw next to a neatly formatted `Esc` and `?`.
     * Writing the glyphs directly is the honest option for an entry that documents
     * someone else's shortcut rather than declaring one of ours — nothing parses this,
     * because nothing binds it.
     *
     * Platform-aware anyway: a Mac user reading "Ctrl" would try the wrong key.
     */
    defaultBinding: IS_APPLE_PLATFORM ? '⌘ +  /  ⌘ −' : 'Ctrl +  /  Ctrl −',
  },
  {
    key: 'dismiss',
    label: 'Close dialog or menu',
    description: 'Dismisses the frontmost dialog, menu, or popover. Stacked surfaces close one at a time.',
    rebindable: false,
    defaultBinding: 'Esc',
  },
]

/** The rebindable subset, in catalog order. The settings card's editable rows. */
export const REBINDABLE_SHORTCUTS: readonly RebindableShortcutEntry[] = SHORTCUT_CATALOG.filter(
  (entry): entry is RebindableShortcutEntry => entry.rebindable,
)

/** The fixed subset, in catalog order. Rendered read-only on both surfaces. */
export const FIXED_SHORTCUTS: readonly FixedShortcutEntry[] = SHORTCUT_CATALOG.filter(
  (entry): entry is FixedShortcutEntry => !entry.rebindable,
)

export const getShortcutCatalogEntry = (key: ShortcutKey): ShortcutCatalogEntry | undefined =>
  SHORTCUT_CATALOG.find(entry => entry.key === key)

/** Defaults keyed for storage — derived from the catalog so a new rebindable
 *  entry cannot be added without its default coming along. */
export const DEFAULT_SHORTCUT_BINDINGS = Object.fromEntries(
  REBINDABLE_SHORTCUTS.map(entry => [entry.key, entry.defaultBinding]),
) as Record<RebindableShortcutKey, string>

